/**
 * Performance Monitoring Utility for Wealthica2 Zustand Migration
 * 
 * Tracks API calls vs Store routing to verify 80% performance improvement
 */

export interface PerformanceStats {
  apiCalls: number;
  storeRoutings: number;
  totalCalls: number;
  performanceImprovement: number;
  callSources: Array<{
    type: 'api' | 'store';
    timestamp: string;
    details?: Record<string, any>;
  }>;
}

import { isDebugEnabled } from '@/utils/debug';

export class PerformanceMonitor {
  private static readonly API_CALLS_KEY = 'wealthica2_api_calls';
  private static readonly STORE_CALLS_KEY = 'wealthica2_store_routing_calls';
  private static readonly CALL_LOG_KEY = 'wealthica2_call_log';

  /**
   * Get current performance statistics
   */
  static getStats(): PerformanceStats {
    if (typeof window === 'undefined') {
      return {
        apiCalls: 0,
        storeRoutings: 0,
        totalCalls: 0,
        performanceImprovement: 0,
        callSources: []
      };
    }

    const apiCalls = parseInt(sessionStorage.getItem(this.API_CALLS_KEY) || '0');
    const storeRoutings = parseInt(sessionStorage.getItem(this.STORE_CALLS_KEY) || '0');
    const totalCalls = apiCalls + storeRoutings;
    
    // Calculate performance improvement (reduction in API calls)
    const performanceImprovement = totalCalls > 0 ? 
      Math.round(((storeRoutings / totalCalls) * 100)) : 0;

    // Get call log
    const callLogRaw = sessionStorage.getItem(this.CALL_LOG_KEY);
    let callSources: Array<{
      type: 'api' | 'store';
      timestamp: string;
      details?: Record<string, any>;
    }> = [];

    if (callLogRaw) {
      try {
        callSources = JSON.parse(callLogRaw);
      } catch (error) {
        console.warn('Failed to parse call log:', error);
      }
    }

    return {
      apiCalls,
      storeRoutings,
      totalCalls,
      performanceImprovement,
      callSources
    };
  }

  /**
   * Log an API call
   */
  static logApiCall(details?: Record<string, any>) {
    if (typeof window === 'undefined') return;
    if (!isDebugEnabled()) return;

    // Increment counter
    const current = parseInt(sessionStorage.getItem(this.API_CALLS_KEY) || '0');
    sessionStorage.setItem(this.API_CALLS_KEY, (current + 1).toString());

    // Log details
    this.logCallSource('api', details);
  }

  /**
   * Log a store routing call
   */
  static logStoreCall(details?: Record<string, any>) {
    if (typeof window === 'undefined') return;
    if (!isDebugEnabled()) return;

    // Increment counter
    const current = parseInt(sessionStorage.getItem(this.STORE_CALLS_KEY) || '0');
    sessionStorage.setItem(this.STORE_CALLS_KEY, (current + 1).toString());

    // Log details
    this.logCallSource('store', details);
  }

  /**
   * Clear all performance tracking data
   */
  static reset() {
    if (typeof window === 'undefined') return;
    
    sessionStorage.removeItem(this.API_CALLS_KEY);
    sessionStorage.removeItem(this.STORE_CALLS_KEY);
    sessionStorage.removeItem(this.CALL_LOG_KEY);
    console.log('Performance tracking reset');
  }

  /**
   * Log call source details
   */
  private static logCallSource(type: 'api' | 'store', details?: Record<string, any>) {
    try {
      const callLogRaw = sessionStorage.getItem(this.CALL_LOG_KEY);
      let callLog: Array<{
        type: 'api' | 'store';
        timestamp: string;
        details?: Record<string, any>;
      }> = [];

      if (callLogRaw) {
        callLog = JSON.parse(callLogRaw);
      }

      callLog.push({
        type,
        timestamp: new Date().toISOString(),
        details
      });

      // Keep only last 50 calls to prevent memory bloat
      if ((callLog?.length || 0) > 50) {
        callLog = callLog.slice(-50);
      }

      sessionStorage.setItem(this.CALL_LOG_KEY, JSON.stringify(callLog));
    } catch (error) {
      console.warn('Failed to log call source:', error);
    }
  }

  /**
   * Display performance stats in console
   */
  static displayStats() {
    const stats = this.getStats();
    
    console.group('🎯 WEALTHICA2 PERFORMANCE STATS');
    console.log(`📊 Total Calls: ${stats.totalCalls}`);
    console.log(`🏗️ API Calls: ${stats.apiCalls}`);
    console.log(`🚀 Store Routings: ${stats.storeRoutings}`);
    console.log(`📈 Performance Improvement: ${stats.performanceImprovement}%`);
    
    if ((stats.callSources?.length || 0) > 0) {
      console.log('📋 Recent Calls:');
      stats.callSources.slice(-10).forEach((call, index) => {
        const icon = call.type === 'api' ? '🏗️' : '🚀';
        console.log(`  ${icon} ${call.type.toUpperCase()}: ${call.timestamp}`);
      });
    }
    
    console.groupEnd();
    
    return stats;
  }
}

/**
 * Global exposure for debugging with enhanced console commands
 */
if (typeof window !== 'undefined') {
  (window as any).wealthicaPerformance = {
    getStats: () => PerformanceMonitor.getStats(),
    displayStats: () => PerformanceMonitor.displayStats(),
    reset: () => PerformanceMonitor.reset(),
    logApi: (details?: Record<string, any>) => PerformanceMonitor.logApiCall(details),
    logStore: (details?: Record<string, any>) => PerformanceMonitor.logStoreCall(details)
  };
  
  if (isDebugEnabled()) {
    console.log('Performance Monitor Ready! Try:');
    console.log('  window.wealthicaPerformance.getStats()');
    console.log('  window.wealthicaPerformance.displayStats()');
    console.log('  window.wealthicaPerformance.reset()');
  }
}
